import React from 'react';
import { useEffect, useState } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Typography, CircularProgress, Button, Stack, IconButton, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import CreateUserDialog from './CreateUserDialog';
import EditUserDialog from './EditUserDialog';
import DeleteUserDialog from './DeleteUserDialog';
import { fetchAllUsers } from '../../controllers/userManagementController';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const UsersList = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const datePart = date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
        const timePart = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        return `${datePart} - ${timePart}`;
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const data = await fetchAllUsers();
                if (mounted) setRows(data || []);
            } catch (err) {
                if (mounted) setError(err?.message || 'Kullanıcılar alınamadı');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Kullanıcılar</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ width: 16 }} />}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#E7EDF4',
                        boxShadow: 'none',
                        fontSize: 13,
                        borderRadius: '5px',
                        color: '#0E4D94',
                        '&:hover': {
                            backgroundColor: '#CFDBEA',
                            boxShadow: 'none',
                        },
                    }}
                    onClick={() => setOpen(true)}
                >
                    Kullanıcı ekle
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #f5f5f5',
                        py: 1,
                        px: 2.5
                    }}
                >
                    <Table size="small">
                        <TableHead sx={{ bgcolor: 'transparent' }}>
                            <TableRow>
                                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Kullanıcı Adı</TableCell>
                                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Roller</TableCell>
                                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
                                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Oluşturulma Tarihi</TableCell>
                                <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: 3 }).map((_, index) => (
                                    <TableRow
                                        key={`skeleton-${index}`}
                                        sx={{
                                            '& .MuiTableCell-root': {
                                                borderBottom: index === 2 ? 'none' : '1px dashed #e8e8e8',
                                                px: 1.5,
                                                py: 1.5,
                                            },
                                        }}
                                    >
                                        <TableCell>
                                            <Skeleton width={150} height={20} />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Skeleton width={80} height={24} borderRadius={16} />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton width={60} height={24} borderRadius={16} />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton width={120} height={20} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                <Skeleton width={32} height={32} borderRadius={6} />
                                                <Skeleton width={32} height={32} borderRadius={6} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                rows.map((u, index) => (
                                    <TableRow
                                        key={u.id}
                                        hover
                                        sx={{
                                            '& .MuiTableCell-root': {
                                                borderBottom: index === rows.length - 1 ? 'none' : '1px dashed #e8e8e8',
                                                px: 1.5,
                                                py: 1.5,
                                                color: '#444444',
                                                fontWeight: 500,
                                            },
                                        }}
                                    >
                                        <TableCell>{u.username}</TableCell>
                                        <TableCell>
                                            {u.roles?.map((r) => (
                                                <Chip
                                                    key={r}
                                                    label={r}
                                                    size="small"
                                                    sx={{
                                                        mr: 0.5,
                                                        fontSize: '13px',
                                                        fontWeight: 400,
                                                    }}
                                                />
                                            ))}
                                        </TableCell>
                                        <TableCell>
                                            {u.isActive ? (
                                                <Chip
                                                    label="Aktif"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#DFFFEA',
                                                        color: '#17C653',
                                                        fontWeight: 600,
                                                        fontSize: '12px'
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    label="Pasif"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#FFEEF3',
                                                        color: '#F8285A',
                                                        fontWeight: 600,
                                                        fontSize: '12px'
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>{formatDate(u.createdAt)}</TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                <Tooltip title="Düzenle">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => { setSelected(u); setEditOpen(true); }}
                                                        aria-label="Düzenle"
                                                        sx={{
                                                            backgroundColor: '#E7EDF4',
                                                            borderRadius: '6px',
                                                            width: 32,
                                                            height: 32,
                                                            '&:hover': {
                                                                backgroundColor: '#CFDBEA',
                                                            },
                                                            '& svg': {
                                                                fontSize: '16px',
                                                                color: '#0E4D94',
                                                            },
                                                        }}
                                                    >
                                                        <EditOutlinedIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Sil">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => { setSelected(u); setConfirmOpen(true); }}
                                                        aria-label="Sil"
                                                        sx={{
                                                            backgroundColor: '#FFEEF3',
                                                            borderRadius: '6px',
                                                            width: 32,
                                                            height: 32,
                                                            '&:hover': {
                                                                backgroundColor: '#FFD6E0',
                                                            },
                                                            '& svg': {
                                                                fontSize: '16px',
                                                                color: '#F8285A',
                                                            },
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <CreateUserDialog
                open={open}
                onClose={() => setOpen(false)}
                onCreated={() => {
                    // refresh list
                    setLoading(true);
                    setError('');
                    fetchAllUsers().then(setRows).catch((e) => setError(e?.message || 'Kullanıcılar alınamadı')).finally(() => setLoading(false));
                }}
            />
            <EditUserDialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                user={selected}
                onUpdated={() => {
                    setLoading(true);
                    setError('');
                    fetchAllUsers().then(setRows).catch((e) => setError(e?.message || 'Kullanıcılar alınamadı')).finally(() => setLoading(false));
                }}
            />
            <DeleteUserDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                user={selected}
                onDeleted={() => {
                    setLoading(true);
                    setError('');
                    fetchAllUsers().then(setRows).catch((e) => setError(e?.message || 'Kullanıcılar alınamadı')).finally(() => setLoading(false));
                }}
            />
        </Box>
    );
};

export default UsersList;


